"use client";

import { Check, ChevronDown } from "lucide-react";
import { useMemo, useState, useRef, useEffect } from "react";

export interface MultiSelectProps {
  options: { label: string; value: string }[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select options...",
  className = "",
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((item) => item !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const displayText = useMemo(() => {
    if (selected.length === 0) return placeholder;
    
    // Get labels for selected items
    const selectedOptions = options.filter(opt => selected.includes(opt.value));
    
    if (selectedOptions.length === 1) {
      return selectedOptions[0].label;
    }
    
    return `${selected.length} selected`;
  }, [selected, options, placeholder]);

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-black/5 disabled:opacity-50"
      >
        <span className="truncate block text-left">
          {displayText}
        </span>
        <ChevronDown className="h-4 w-4 text-neutral-500 flex-shrink-0 ml-2" />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-neutral-200 bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-neutral-500">
              No options available
            </div>
          ) : (
            options.map((option) => {
              const isSelected = selected.includes(option.value);
              return (
                <div
                  key={option.value}
                  className={`relative flex cursor-pointer select-none items-center py-2 pl-3 pr-9 text-sm hover:bg-neutral-100 ${
                    isSelected ? "bg-neutral-50 text-neutral-900 font-medium" : "text-neutral-600"
                  }`}
                  onClick={() => handleSelect(option.value)}
                >
                  <span className="block truncate">{option.label}</span>
                  {isSelected && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-900">
                      <Check className="h-4 w-4" />
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
